import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { App } from "@capacitor/app";
import { PushNotifications } from "@capacitor/push-notifications";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { getAuth, updateProfile, updateEmail } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { useFunctions } from "reactfire";
import { Capacitor } from "@capacitor/core";
import { showToast } from "utils/utils";
import { useTranslation } from "react-i18next";
import { useGame } from "context/gameContext";
import { useUser } from "reactfire";
import { useFirestore } from "reactfire";
import { themes } from "utils/utils";
import { Purchases } from "@revenuecat/purchases-capacitor";
import { useIonRouter } from "@ionic/react";

import { AdMob } from "@capacitor-community/admob";

const InitializeUrlListener = () => {
  const history = useHistory();

  const { status, data: user } = useUser();

  const {
    state: { userGameData, settings },
    dispatch,
  } = useGame();
  const { t } = useTranslation();

  const [pendingInvite, setPendingInvite] = useState(undefined);
  const [pendingInviteRefer, setPendingInviteRefer] = useState(undefined);

  const firestore = useFirestore();
  const ionRouter = useIonRouter();

  const functions = useFunctions();
  const inviteUserToLeague = httpsCallable(functions, "inviteToLeague", {
    timeout: 530000,
  });

  const updateUserPushTokens = httpsCallable(
    functions,
    "updateUserPushTokens",
    {
      timeout: 530000,
    }
  );

  const initializeAuthState = () => {
    if (Capacitor.isNativePlatform()) {
      FirebaseAuthentication.removeAllListeners().then(() => {
        FirebaseAuthentication.addListener("authStateChange", (result) => {});
      });
    }
  };

  useEffect(() => {
    initializeAuthState();
    //initializeRevenueCat();
  }, []);

  useEffect(() => {
    const isAndroid = Capacitor.getPlatform() === "android";

    if (!isAndroid) {
      return;
    }

    App.addListener("ionBackButton", (ev) => {
      ev.detail.register(5, (processNextHandler) => {
        processNextHandler();
      });

      ev.detail.register(10, (processNextHandler) => {
        processNextHandler();
      });

      ev.detail.register(-1, () => {
        if (!ionRouter.canGoBack()) {
          App.exitApp();
        }
      });
    });

    return () => App.removeAllListeners();
  }, []);

  useEffect(() => {
    //todo called twice
    if (!userGameData) return;
    if (!pendingInvite) return;
    if (!user) return;

    let data = {
      type: "invite",
      toUserId: user.uid,
      fromUser: {
        name: null,
        id: pendingInviteRefer,
        photo: null,
      },
      leagueId: pendingInvite,
    };

    inviteUserToLeague(data)
      .then((result) => {
        const data = result.data;
        const sanitizedMessage = data.text;
        setPendingInvite(undefined);
        setPendingInviteRefer(undefined);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [userGameData, user, pendingInvite]);

  const registerNotifications = async () => {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === "prompt") {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== "granted") {
      throw new Error("User denied permissions!");
    }

    await PushNotifications.register().then((res) => {
      addListeners();
    });
  };

  const addListeners = async () => {
    await PushNotifications.removeAllListeners();
    await PushNotifications.addListener("registration", (token) => {
      if (token && token.value && user && user.uid) {
        let data = {
          userId: user.uid,
          token: token.value,
        };
        return updateUserPushTokens(data)
          .then(async (result) => {
            return result;
          })
          .catch((error) => {
            console.log(error);
          });
      } else console.log(token + "is not valid");
    });

    await PushNotifications.addListener("registrationError", (err) => {
      console.error("Registration error: ", err.error);
    });

    await PushNotifications.addListener(
      "pushNotificationReceived",
      (notification) => {
        const data = notification.data.data; //data.learnerId
        let notData = JSON.parse(data);
        let bodyTitle = notData?.body_loc_key;
        let bodyKeys = notData?.body_loc_args;
        let titleKeys = notData?.title_loc_args;

        let string;
        let args = {};
        if (bodyTitle == "PUSH_INVITE_BODY") {
          string = "push.invite.body";
          args = { user: bodyKeys[0], tournament: bodyKeys[1] };
        } else if (bodyTitle == "PUSH_TOURNAMENTSETTLED_BODY") {
          string = "push.tournamentSettled.title";
          args = { tournament: titleKeys[0] };
        } else if (bodyTitle == "PUSH_ROUNDSETTLED_BODY") {
          string = "push.roundSettled.title";
          args = { tournament: titleKeys[0], round: titleKeys[1] };
        }

        if (string && args) {
          showToast(t(string, args));
        }
      }
    );

    await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (notification) => {
        const data = notification?.notification?.data?.data; //data.learnerId
        let notData = JSON.parse(data);

        let type = notData?.type;
        if (type == "invite") {
          history.push("/");
        } else if (type == "roundSettled") {
          let url = notData?.url;
          //navigate(url);
          history.push(url);
        }
      }
    );
  };

  async function logInUser(id) {
    try {
      const { customerInfo, created } = await Purchases.logIn({
        appUserID: id,
      });

      return customerInfo;
    } catch (e) {
      //Alert.alert('Error fetching customer info', e.message);
    }
  }

  function checkSubscriptionStatus(customerInfo) {
    try {
      //const customer = await Purchases.getCustomerInfo();
      const pro = customerInfo?.entitlements.all["premium"];

      if (pro && pro.isActive) {
        dispatch({
          type: "setPremium",
          value: { isActive: true, expires: pro?.expirationDate },
        });
      } else {
        dispatch({
          type: "setPremium",
          value: { isActive: false, expires: null },
        });
      }
    } catch (e) {
      console.log(e);
    }
  }

  const addSubscriptionListeners = async () => {
    logInUser(user?.uid);
    await Purchases.addCustomerInfoUpdateListener((customerInfo) => {
      checkSubscriptionStatus(customerInfo);
    });
  };

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !user?.uid) {
      return;
    }
    addSubscriptionListeners();
  }, [user?.uid]);

  useEffect(() => {
    if (!userGameData) return;

    let userTheme = themes[userGameData.theme];
    let userImage = userGameData?.photo_256x256;
    if (userTheme) {
      window.localStorage.setItem("theme", JSON.stringify(userTheme));

      dispatch({ type: "setTheme", value: userTheme });
    }

    if (userImage) {
      updateProfile(getAuth().currentUser, {
        photoURL: userImage,
      });
    }
    if (userGameData?.name) {
      updateProfile(getAuth().currentUser, {
        displayName: userGameData?.name,
      });
    }
    if (userGameData?.email) {
      updateEmail(getAuth().currentUser, userGameData?.email);
    }
  }, [userGameData]);

  useEffect(() => {
    if (!userGameData?.id) return;

    /*     if (Capacitor.isNativePlatform()) {
      initializeAdMob();
    } */

    const isPushNotificationsAvailable =
      Capacitor.isPluginAvailable("PushNotifications");

    if (Capacitor.isNativePlatform() && isPushNotificationsAvailable) {
      registerNotifications();
    }
  }, [userGameData?.id]);

  return null;
};

export default InitializeUrlListener;
